/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/**
 *
 * TransactionTime
 *
 */

import React, { memo, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { useSearchParams } from 'react-router-dom';
import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import { Button, Conditional, InputWithSuffix } from '@cashfree-intl/coherent';
import CustomIcon from 'components/CustomIcons/main';
import Loader from 'components/Loader';
import { getItemFromLocalStorage } from 'utils/common';
import Analytics from 'utils/analytics';
import makeSelectTransactionTime from './selectors';
import reducer from './reducer';
import saga from './saga';
import {
  enableLoaderAction,
  disableLoaderAction,
  setTransactionCompleteTimeAction,
  getTransactionCompleteTimeAction,
} from './actions';

type TransactionTimeType = {
  isLoading: boolean;
};

interface TransactionTimeProps {
  getTransactionCompleteTime: (data: any) => void;
  setTransactionCompleteTime: (data: any) => void;
  enableLoader: () => void;
  disableLoader: () => void;
  transactionTime: TransactionTimeType;
}

export function TransactionTime({
  enableLoader,
  disableLoader,
  setTransactionCompleteTime,
  getTransactionCompleteTime,
  transactionTime,
}: TransactionTimeProps) {
  useInjectReducer({ key: 'transactionTime', reducer });
  useInjectSaga({ key: 'transactionTime', saga, mode: '' });

  const [searchParams] = useSearchParams();

  const env = searchParams.get('env') || '';

  const { isLoading } = transactionTime;
  const merchantId = getItemFromLocalStorage('merchantId');

  const [isTTLValid, resetTTLValid] = useState(false);
  const [expandItems, resetExpandItems] = useState({
    ORDER: true,
    TRANSACTION: false,
  });
  const [transactionCompleteTime, resetTransactionCompleteTime] = useState({
    transactionCompleteHours: '',
    transactionCompleteMins: '',
  });

  useEffect(() => {
    enableLoader();
    getTransactionCompleteTime({ merchantId, cb: getModifiedPreferencesData });
  }, [env]);

  const handleExpandItems = key => {
    resetExpandItems({
      ...expandItems,
      [key]: !expandItems[key],
    });
  };

  const getTTLValid = ({ name, value }) => {
    let {
      transactionCompleteHours,
      transactionCompleteMins,
    } = transactionCompleteTime;
    let transactionCompleteHoursInt = parseInt(transactionCompleteHours, 10);
    let transactionCompleteMinsInt = parseInt(transactionCompleteMins, 10);
    if (name === 'transactionCompleteHours') {
      transactionCompleteHours = value;
      transactionCompleteHoursInt = parseInt(value, 10);
    }
    if (name === 'transactionCompleteMins') {
      transactionCompleteMins = value;
      transactionCompleteMinsInt = parseInt(value, 10);
    }
    return (
      ((transactionCompleteHoursInt === 0 &&
        transactionCompleteMinsInt >= 10 &&
        transactionCompleteMinsInt < 60) ||
        (transactionCompleteHoursInt > 0 &&
          transactionCompleteHoursInt < 24 &&
          transactionCompleteMinsInt >= 0 &&
          transactionCompleteMinsInt < 60) ||
        (transactionCompleteHoursInt === 24 &&
          transactionCompleteMinsInt === 0)) &&
      !/\./.test(transactionCompleteHours) &&
      !/\./.test(transactionCompleteMins)
    );
  };

  const handleTTLChange = (e, data) => {
    resetTransactionCompleteTime({
      ...transactionCompleteTime,
      [data.name]: data.value,
    });
    resetTTLValid(getTTLValid(data));
  };

  const renderTTL = () => (
    <div className="preferences-wrapper">
      <div className="preferences-header">
        <div className="text-dark medium-text w-600 preferences-user-select">
          Conflicted Transactions Threshold
        </div>
        <div
          data-testId="transaction-div"
          onClick={() => handleExpandItems('TRANSACTION')}
          className="preferences-chevron-icon"
          data-event-name="Form_Preference_Transaction_Icon_Expand"
          data-ignore-inner-text="true"
        >
          <CustomIcon
            componentName="CHEVRONDOWN"
            direction={expandItems.TRANSACTION ? 'up' : 'down'}
          />
        </div>
      </div>
      <div className="medium-text w-400 preferences-text">
        Any transaction that is success after threshold time will be treated as
        conflicted, and that transaction will be auto-refunded.
      </div>
      <Conditional if={expandItems.TRANSACTION}>
        <div className="preferences-time-wrapper">
          <div className="preferences-user-select">Set Time</div>
          <div className="preference-set-time-warpper">
            <InputWithSuffix
              type="number"
              name="transactionCompleteHours"
              value={transactionCompleteTime.transactionCompleteHours}
              onChange={handleTTLChange}
              className={`preferences-time ${
                !isTTLValid ? 'error_border' : ''
              }`}
              label={<span className="ml--2">hours</span>}
              labelPosition="right"
            />
            <span className="normal-text w-600 preferences-colon">:</span>
            <InputWithSuffix
              type="number"
              name="transactionCompleteMins"
              value={transactionCompleteTime.transactionCompleteMins}
              onChange={handleTTLChange}
              className={`preferences-time ${
                !isTTLValid ? 'error_border' : ''
              }`}
              label={<span>mins</span>}
              labelPosition="right"
            />
          </div>
          <Conditional if={!isTTLValid}>
            <div className="preferences-error">
              Time should be between 10 mins and 24 hrs
            </div>
          </Conditional>
        </div>
      </Conditional>
    </div>
  );

  const getModifiedPreferencesData = data => {
    const { config_value: transactionCompletionTimeMins } = data;
    if (transactionCompletionTimeMins !== '') {
      resetTransactionCompleteTime({
        transactionCompleteHours:
          Math.trunc(transactionCompletionTimeMins / 60) < 10
            ? `0${Math.trunc(transactionCompletionTimeMins / 60)}`
            : `${Math.trunc(transactionCompletionTimeMins / 60)}`,
        transactionCompleteMins:
          transactionCompletionTimeMins % 60 < 10
            ? `0${transactionCompletionTimeMins % 60}`
            : `${transactionCompletionTimeMins % 60}`,
      });
      resetTTLValid(true);
    }
    disableLoader();
  };

  const handleSaveChanges = () => {
    Analytics.trackEventWithNewNomenclature({
      eventName: 'Transaction_Time_Save_Changes_Button_Click',
    });
    enableLoader();
    if (
      getTTLValid({
        name: '',
        value: '',
      })
    ) {
      const transactionCompleteTimeValue =
        parseInt(transactionCompleteTime.transactionCompleteHours, 10) * 60 +
        parseInt(transactionCompleteTime.transactionCompleteMins, 10);
      setTransactionCompleteTime({
        data: {
          transactionCompletionTimeMins: `${transactionCompleteTimeValue}`,
          merchantId,
        },
        cb: getModifiedPreferencesData,
      });
    } else {
      resetTTLValid(false);
      disableLoader();
    }
  };

  return (
    <div>
      <div className="preferences-container">
        <Loader active={isLoading} />
        <Button
          primary
          className="save-changes-btn"
          onClick={handleSaveChanges}
          disabled={!isTTLValid}
          data-event-name="Primary_Button_SaveChanges"
        >
          Save Changes
        </Button>

        {renderTTL()}
      </div>
    </div>
  );
}

const mapStateToProps = createStructuredSelector({
  transactionTime: makeSelectTransactionTime(),
});

function mapDispatchToProps(dispatch) {
  return {
    enableLoader: () => dispatch(enableLoaderAction()),
    disableLoader: () => dispatch(disableLoaderAction()),
    setTransactionCompleteTime: data =>
      dispatch(setTransactionCompleteTimeAction(data)),
    getTransactionCompleteTime: data =>
      dispatch(getTransactionCompleteTimeAction(data)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(TransactionTime);
